import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { StudentsReport } from '@chancenwerk/shared/StudentsReport.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import type { BoardComponentUserCard } from './board-component-user-card.js';
/**
 * @customElement board-component-user-pool
 */
@customElement('board-component-user-pool')
export class BoardComponentUserPool extends ChemistryLitElement {
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          '.scrollbar': {
            '&::-webkit-scrollbar': { width: '18px' },
            '&::-webkit-scrollbar-track': { background: 'rgb(229, 229, 229)' },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '50px',
              border: '6px solid rgb(229, 229, 229)',
              backgroundClip: 'padding-box',
            },
            '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
          },
        },
      },
    ];
  }

  @property({ attribute: true, reflect: true }) public label = 'Kein Label gesetzt!';
  @property({ attribute: true, reflect: false }) public userType: 'US' | 'OS' | 'LeKo' | 'SchuKo' | 'DiKu' = 'US';
  @property({ attribute: false }) public users = [] as ChancenwerkUser[];
  @property({ attribute: false }) public kpfID: Maybe<string>;
  @property({ attribute: false }) public filterFn: Maybe<(user: ChancenwerkUser) => boolean> = null;
  @property({ attribute: false }) public grayed: Set<string> = new Set();
  @property({ attribute: false }) public courses: Course[] = [];
  @property({ attribute: false }) public showChoosenCourses = false;

  @state() _studentsReports: Maybe<StudentsReport> = null;
  @state() _search = new RXController(this, '');

  override connectedCallback() {
    super.connectedCallback();

    if (this.kpfID) {
      StudentsReport.getStudentsReportForKPF(this.kpfID)(StudentsReport.allFields)
        .then(r => {
          this._studentsReports = r;
        })
        .catch(console.error);
    }
  }

  private _searchInput() {
    if (this.users.length < 5 || this.filterFn) {
      return nothing;
    }

    const placeholder = ['US', 'OS'].includes(this.userType) ? 'Name oder Klasse...' : 'Name...';
    return html` <cr-input
      placeholder=${placeholder}
      .value=${this._search}
      @value-picked=${(e: ValueEvent<Maybe<string>>) => {
        this._search.next(e.detail.value ?? '');
      }}
      ${css({ marginBottom: this.spacing.sm })}
    >
    </cr-input>`;
  }

  private _filter() {
    if (!this.filterFn) {
      return nothing;
    }

    return html`<cr-flex
      horizontal
      space-between
      crossaxis-center
      ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}
    >
      <cr-text>Filter aktiv</cr-text>
      <cr-button mode="icon" icon="close" @click=${() => this._dispatchFilterClosed()}></cr-button>
    </cr-flex>`;
  }

  private _duplicateFn(staticEl: BoardComponentUserCard, movingEl: BoardComponentUserCard) {
    movingEl.user = staticEl.user;
  }

  private async _onDropped(e: any) {
    const detail = (e as { detail: { user: ChancenwerkUser; oldAssignments: { value: Course[] } } }).detail;

    // TODO this should be better handled somewhere else
    if (this.userType === 'DiKu') {
      for (const c of e.detail.oldAssignments.value) {
        c.diKuIDs = c.diKuIDs?.filter(id => id !== e.detail.user._id);
        c.diKus = c.diKus?.filter(u => u._id !== e.detail.user._id);
      }
    } else {
      Course.assignUserToCourse(detail.user, null, {
        arrayToAssign: this.userType,
        oldCourses: detail.oldAssignments.value,
      });
    }
    // Optimistisch anzeigen, damit der Drop sofort sichtbar ist. Ob der Nutzer wirklich in den Pool
    // gehoert, entscheidet aber die Seite ueber ihren Filter - die muss also neu rechnen, sonst
    // bleibt hier jemand stehen, den der Filter laengst aussortiert haette.
    if (!this.users.some(u => u._id === e.detail.user._id)) this.users.push(detail.user);
    this.requestUpdate();
    this.dispatchEvent(new CustomEvent('pool-changed', { detail: { user: detail.user } }));
  }

  private _dispatchFilterClosed() {
    this.dispatchEvent(new CustomEvent('filter-closed'));
  }

  private _userIsInCourse(course: Course, user: ChancenwerkUser): boolean {
    switch (this.userType) {
      case 'SchuKo':
        return course.schuKoIDs?.includes(user._id) ?? false;
      case 'LeKo':
        return course.leKoIDs?.includes(user._id) ?? false;
      case 'OS':
        return course.osStudentIDs.includes(user._id);
      case 'US':
        return course.usStudentIDs.includes(user._id);
      case 'DiKu':
        return (course as Course & { diKuIDs?: string[] }).diKuIDs?.includes(user._id) ?? false;
      default:
        return false;
    }
  }

  private _assignedCourses(user: ChancenwerkUser): Course[] {
    return this.courses.filter(c => this._userIsInCourse(c, user));
  }

  /**
   * Students without a single Durchführungstag in their profile still belong in the pool - they just
   * cannot be placed sensibly yet. Flag them so the coordinator sees why they are sitting here.
   * Only the pools that were handed the profile day wishes can judge this.
   */
  private _missingDaysWarning(user: ChancenwerkUser): Maybe<string> {
    if (!this.showChoosenCourses || !(user instanceof Student)) return null;
    if ((user.lernfoerderungDuerchfuehrungsTagChoosen ?? []).length) return null;
    return 'Keine Durchführungstage im Profil ausgewählt. Bitte im CHANCENraum nachtragen!';
  }

  private _items() {
    return repeat(
      this.users,
      user => user._id,
      user => {
        if (this._search.value && !this.filterFn) {
          const inVorname = user.vorname?.toLocaleLowerCase().includes(this._search.value.toLocaleLowerCase());
          const inNachname = user.nachname?.toLocaleLowerCase().includes(this._search.value.toLocaleLowerCase());
          const inSchoolClass =
            user instanceof Student && user.schoolClass.includes(this._search.value.toLocaleLowerCase());

          if (!inVorname && !inNachname && !inSchoolClass) {
            return nothing;
          }
        } else if (this.filterFn && !this.filterFn(user)) {
          return nothing;
        }

        return html`
          <board-component-user-card
            ${css({ opacity: this.grayed.has(user._id) ? 0.5 : 1 })}
            ?US=${this.userType === 'US'}
            ?OS=${this.userType === 'OS'}
            ?LeKo=${this.userType === 'LeKo'}
            ?SchuKo=${this.userType === 'SchuKo'}
            ?DiKu=${this.userType === 'DiKu'}
            pool
            .user=${user}
            .studentsReport=${this._studentsReports}
            .assignedCourses=${this._assignedCourses(user)}
            .showChoosenCourses=${this.showChoosenCourses}
            .warning=${this._missingDaysWarning(user)}
            .warningTone=${'info'}
          ></board-component-user-card>
        `;
      },
    );
  }

  override render() {
    return html`
      <cr-nospace-toggle-content .label=${this.label}>
        <board-component-drag-drop
          type=${this.userType}
          dropzone-accept-selector="board-component-user-card[${this.userType.toLocaleLowerCase()}][assigned]"
          ${css({ padding: this.spacing.xs, marginRight: this.spacing.md })}
          .allow-from="board-component-user-card[${this.userType.toLocaleLowerCase()}]"
          @dropped=${async e => this._onDropped(e)}
          duplicate
          .duplicateFn=${(a: BoardComponentUserCard, b: BoardComponentUserCard) => this._duplicateFn(a, b)}
        >
          ${this._searchInput()} ${this._filter()}
          <cr-flex
            class="scrollbar"
            space="sm"
            ${css({
              gap: '4px',
              minHeight: '8rem',
              maxHeight: '24rem',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              touchAction: 'none',
            })}
          >
            ${this._items()}
          </cr-flex>
        </board-component-drag-drop>
      </cr-nospace-toggle-content>
    `;
  }
}
